import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface BookingFormSettings {
  max_people: number | null;
  max_people_message: string | null;
}

/**
 * Client for the booking form settings
 * (Back_End_Server /api/v1/booking-form-settings).
 *
 * One settings record per tenant: the party-size cap for the public booking
 * form and the custom message shown when a guest exceeds it. Management calls
 * are JWT-authed — the AuthInterceptor attaches the token for every
 * environment.baseUrl request. getPublicSettings is the unauthed endpoint the
 * booking form itself uses; a tenant that never saved settings gets nulls,
 * which the form treats as 'no limit'.
 */
@Injectable({ providedIn: 'root' })
export class BookingFormSettingsService {
  private readonly apiUrl = `${environment.baseUrl}/api/v1/booking-form-settings`;

  constructor(private http: HttpClient) {}

  /** Settings of the logged-in tenant (nulls when never saved). */
  getSettings(): Observable<BookingFormSettings> {
    return this.http
      .get<{ message: string; data: BookingFormSettings }>(this.apiUrl)
      .pipe(map((res) => res.data));
  }

  /** Settings for a restaurant's booking form — public, no token. */
  getPublicSettings(userId: number): Observable<BookingFormSettings> {
    return this.http
      .get<{ message: string; data: BookingFormSettings }>(
        `${this.apiUrl}/public/${userId}`
      )
      .pipe(map((res) => res.data));
  }

  /** Upsert the logged-in tenant's settings; null max_people disables the limit. */
  saveSettings(settings: {
    max_people: number | null;
    max_people_message: string | null;
  }): Observable<BookingFormSettings> {
    return this.http
      .put<{ message: string; data: BookingFormSettings }>(this.apiUrl, settings)
      .pipe(map((res) => res.data));
  }
}
